import React, { useRef } from "react";
import { Box, Text, Image, Flex, useColorModeValue } from "@chakra-ui/react";
import { motion, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";
// @ts-ignore
import map from "../assets/map.png";

const MotionBox = motion(Box);

const Implantations = () => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const isInView = useInView(ref, { once: false, margin: "-80px" });

    const bg        = useColorModeValue("#03111F", "#03111F");
    const textColor = useColorModeValue("#E8E0D0", "#E8E0D0");

    // Quelques stats / chiffres clés
    const stats = [
        { value: "3", label: t("implantationsStat1") || "Pays" },
        { value: "6", label: t("implantationsStat2") || "Villes" },
        { value: "2021", label: t("implantationsStat3") || "Fondée en" },
    ];

    return (
        <Box
            id="implantations"
            ref={ref}
            bg={bg}
            py={{ base: 24, md: 40 }}
            px={{ base: 6, md: 16 }}
            position="relative"
            overflow="hidden"
        >
            {/* ── Ambient glow ── */}
            <MotionBox
                position="absolute"
                top="10%"
                left="-100px"
                w="500px"
                h="500px"
                bg={useColorModeValue(
                    "radial-gradient(ellipse, rgba(201,168,76,0.06) 0%, transparent 70%)",
                    "radial-gradient(ellipse, rgba(201,168,76,0.04) 0%, transparent 70%)"
                )}
                pointerEvents="none"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 1.8 }}
            />

            {/* ── Numéro décoratif ── */}
            <MotionBox
                position="absolute"
                left="-10px"
                top="50%"
                transform="translateY(-50%)"
                pointerEvents="none"
                userSelect="none"
                initial={{ opacity: 0, x: -60 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -60 }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
                <Text
                    fontSize={{ base: "120px", md: "200px" }}
                    fontWeight="900"
                    lineHeight="1"
                    letterSpacing="-0.05em"
                    color={useColorModeValue("rgba(10,22,40,0.04)", "rgba(255,255,255,0.025)")}
                >
                    05
                </Text>
            </MotionBox>

            {/* ── Layout principal ── */}
            <Flex
                direction={{ base: "column", lg: "row" }}
                align="center"
                gap={{ base: 16, lg: 24 }}
                maxW="1300px"
                mx="auto"
                position="relative"
                zIndex={1}
            >
                {/* ── Colonne gauche : texte + stats ── */}
                <Box flex="1" maxW={{ lg: "420px" }}>
                    {/* Eyebrow */}
                    <motion.div
                        initial={{ opacity: 0, x: -24 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -24 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <Text
                            fontSize="10px"
                            letterSpacing="0.4em"
                            textTransform="uppercase"
                            color={textColor}
                            opacity={0.35}
                            fontWeight="500"
                            mb={5}
                        >
                            {t("ImplantationsTitre")}
                        </Text>
                    </motion.div>

                    {/* Titre */}
                    <motion.div
                        initial={{ opacity: 0, x: -32 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -32 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <Text
                            fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                            fontWeight="200"
                            color={textColor}
                            letterSpacing="-0.04em"
                            lineHeight="1.05"
                            mb={6}
                        >
                            {t("implantationsHeading1")}{" "}
                            <Box as="span" fontWeight="800">{t("implantationsHeading2")}</Box>
                        </Text>
                    </motion.div>

                    {/* Séparateur */}
                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                        transition={{ duration: 0.8, delay: 0.22, ease: [0.16, 1, 0.3, 1] }}
                        style={{ originX: 0, marginBottom: "2rem" }}
                    >
                        <Box w="48px" h="1px" bg="linear-gradient(90deg, #C9A84C, #8B9E6E)" opacity={0.7} />
                    </motion.div>

                    {/* Texte descriptif */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                        transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <Text
                            fontSize={{ base: "md", md: "lg" }}
                            color={textColor}
                            opacity={0.6}
                            lineHeight="1.9"
                            fontWeight="300"
                            mb={12}
                        >
                            {t("ImplantationsTexte") || "Groupe HMP déploie son expertise à travers plusieurs implantations stratégiques, au plus proche de ses clients et chantiers."}
                        </Text>
                    </motion.div>

                    {/* Stats */}
                    <Flex gap={8} wrap="wrap">
                        {stats.map((s, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.7, delay: 0.4 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                            >
                                <Box>
                                    <Text
                                        fontSize={{ base: "3xl", md: "4xl" }}
                                        fontWeight="700"
                                        color={textColor}
                                        letterSpacing="-0.03em"
                                        lineHeight="1"
                                        mb={1}
                                    >
                                        {s.value}
                                        <Box as="span" color="#C9A84C" fontSize="60%">+</Box>
                                    </Text>
                                    <Text
                                        fontSize="9px"
                                        letterSpacing="0.3em"
                                        textTransform="uppercase"
                                        color={textColor}
                                        opacity={0.4}
                                        fontWeight="500"
                                    >
                                        {s.label}
                                    </Text>
                                </Box>
                            </motion.div>
                        ))}
                    </Flex>
                </Box>

                {/* ── Colonne droite : carte ── */}
                <Box flex="1.2" position="relative">
                    {/* Halo derrière la carte */}
                    <Box
                        position="absolute"
                        inset="-40px"
                        bg={useColorModeValue(
                            "radial-gradient(ellipse at center, rgba(201,168,76,0.08) 0%, transparent 65%)",
                            "radial-gradient(ellipse at center, rgba(201,168,76,0.05) 0%, transparent 65%)"
                        )}
                        pointerEvents="none"
                        zIndex={0}
                    />

                    <motion.div
                        initial={{ opacity: 0, x: 50, scale: 0.96 }}
                        animate={isInView ? { opacity: 1, x: 0, scale: 1 } : { opacity: 0, x: 50, scale: 0.96 }}
                        transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        style={{ position: "relative", zIndex: 1 }}
                    >
                        <Box
                            borderRadius="28px"
                            overflow="hidden"
                            border="1px solid"
                            borderColor={useColorModeValue("rgba(10,22,40,0.08)", "rgba(255,255,255,0.07)")}
                            boxShadow={useColorModeValue(
                                "0 40px 80px rgba(0,0,0,0.12), 0 1px 0 rgba(255,255,255,0.8) inset",
                                "0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)"
                            )}
                            position="relative"
                        >
                            {/* Ligne accent haut */}
                            <Box
                                position="absolute"
                                top={0}
                                left="15%"
                                right="15%"
                                h="1px"
                                bg="linear-gradient(90deg, transparent, #C9A84C80, transparent)"
                                zIndex={2}
                            />

                            <Image
                                src={map}
                                alt="Carte des implantations"
                                w="100%"
                                objectFit="contain"
                                display="block"
                                style={{
                                    filter: useColorModeValue(
                                        "saturate(0.8) brightness(0.97)",
                                        "saturate(0.6) brightness(0.7) contrast(1.1)"
                                    ),
                                }}
                            />
                        </Box>
                    </motion.div>
                </Box>
            </Flex>
        </Box>
    );
};

export default Implantations;